import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import { Company } from '../dtos/company';
import { accessToken, getAuthDetails } from '../utils/helper';
import Cookies from 'universal-cookie';
import { ApiResponseError } from '../dtos/common';

const cookies = new Cookies(null, { path: '/' });

type CompanyListResponse = { data: Company[]; total: number; totalPages: number };
type CompanyMessageResponse = { message: string };

const getApiErrorMessage = (error: unknown) => {
  const apiError = error as ApiResponseError;

  return apiError.response?.data?.message || 'Something Went Wrong!';
};

interface CompanyState {
  companyList: CompanyListResponse;
  allCompaniesForDropdown: Company[]; // Separate state for dropdown data
  loading: boolean;
  error: string | null;
  addCompanyResponse: CompanyMessageResponse;
  addCompanyError: string | null;
  updateCompanyResponse: CompanyMessageResponse;
  updateCompanyError: string | null;
  deleteCompanyResponse: CompanyMessageResponse;
  deleteCompanyError: string | null;
}

const initialState: CompanyState = {
  companyList: { data: [], total: 0, totalPages: 0 },
  allCompaniesForDropdown: [], // Initialize dropdown data
  addCompanyResponse: { message: '' },
  updateCompanyResponse: { message: '' },
  deleteCompanyResponse: { message: '' },
  loading: false,
  error: null,
  addCompanyError: null,
  updateCompanyError: null,
  deleteCompanyError: null,
};

export const getAllCompaniesForDropdownTrigger = createAsyncThunk(
  'get/companies/dropdown',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/companies?limit=1000', accessToken());
      if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const getAllCompaniesTrigger = createAsyncThunk(
  'get/companies',
  async (
    {
      page,
      limit,
      search,
      sortOrder,
      sortField,
    }: {
      page?: number;
      limit?: number;
      search?: string;
      sortOrder?: string;
      sortField?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      // Clean the sortField to remove any malformed characters
      const cleanSortField = sortField ? sortField.split(':')[0] : undefined;
      
      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (cleanSortField) queryParams.append('sortField', cleanSortField);

      const response = await api.get(`/companies?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const addCompanyTrigger = createAsyncThunk(
  '/companies/add',
  async (company: FormData, { rejectWithValue }) => {
    try {
      const response = await api.post<CompanyMessageResponse>(
        '/companies/add',
        company,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getAuthDetails().accessToken}`,
            'Content-Type': 'multipart/form-data',
            'x-csrf-token': cookies.get('XSRF-TOKEN'),
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const updateCompanyTrigger = createAsyncThunk(
  '/companies/update/:id',
  async ({ id, request }: { id: number; request: FormData }, { rejectWithValue }) => {
    try {
      const response = await api.patch<CompanyMessageResponse>(
        `/companies/update/${id}`,
        request,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getAuthDetails().accessToken}`,
            'Content-Type': 'multipart/form-data',
            'x-csrf-token': cookies.get('XSRF-TOKEN'),
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const deleteCompanyTrigger = createAsyncThunk(
  '/companies/delete/:id',
  async (id: number | undefined, { rejectWithValue }) => {
    try {
      const response = await api.delete<CompanyMessageResponse>(
        `/companies/delete/${id}`,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

// Create slice
const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllCompaniesTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllCompaniesTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.companyList = action.payload;
      })
      .addCase(getAllCompaniesTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || action.error.message || 'Failed to load companies';
      })
      .addCase(getAllCompaniesForDropdownTrigger.rejected, (state, action) => {
        state.error =
          (action.payload as string) ||
          action.error.message ||
          'Failed to load companies dropdown';
      })
      .addCase(getAllCompaniesForDropdownTrigger.fulfilled, (state, action) => {
        state.allCompaniesForDropdown = action.payload;
      })
      .addCase(addCompanyTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCompanyTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.addCompanyResponse = action.payload;
      })
      .addCase(addCompanyTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addCompanyError =
          (action.payload as string) || action.error.message || 'Failed to add company';
      })
      .addCase(updateCompanyTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCompanyTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.updateCompanyResponse = action.payload;
      })
      .addCase(updateCompanyTrigger.rejected, (state, action) => {
        state.loading = false;
        state.updateCompanyError =
          (action.payload as string) || action.error.message || 'Failed to update company';
      })
      .addCase(deleteCompanyTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCompanyTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteCompanyResponse = action.payload;
      })
      .addCase(deleteCompanyTrigger.rejected, (state, action) => {
        state.loading = false;
        state.deleteCompanyError =
          (action.payload as string) || action.error.message || 'Failed to delete company';
      });
  },
});

// Export the reducer to be added to the store
export default companySlice.reducer;
